import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  Checkbox
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import { DutyPlannerService } from '../../../services/DutyPlanner/DutyPlannerService'
import { DutyPlannerInput } from '../../../services/DutyPlanner/types'

interface ICommercialConsultantFormModalProps {
  isOpen: boolean
  onClose: () => void
  id: number | null
}

export default function DutyPlannerFormModal({ isOpen, onClose, id }: ICommercialConsultantFormModalProps) {
  const service = new DutyPlannerService()
  const commercialConsultantService = new CommercialConsultantService()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<DutyPlannerInput>({
    title: '',
    date: '',
    has_duty: true,
    consultant_ids: []
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [commercialConsultants, setCommercialConsultants] = useState<{ id: number; name: string }[]>([])

  useEffect(() => {
    commercialConsultantService.list().then((consultants) => {
      setCommercialConsultants(consultants.map((c) => ({ id: c.id, name: c.name })))
    })
  }, [])

  useEffect(() => {
    if (id && isOpen) {
      service
        .get(id)
        .then((data) => {
          setForm({
            title: data.title,
            date: data.date.split('T')[0],
            has_duty: data.has_duty,
            consultant_ids: data.consultants.map((c) => c.id)
          })
          setFormErrors({})
        })
        .catch((err) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const error = err as any
          if (error.response?.status === 404) {
            toast({
              title: 'Feriado/Plantão não encontrado',
              status: 'error',
              duration: 3000,
              isClosable: true
            })
          } else {
            toast({
              title: 'Erro ao carregar feriado/plantão',
              status: 'error',
              duration: 3000,
              isClosable: true
            })
          }
          onClose()
        })
    } else {
      setForm({
        title: '',
        date: '',
        has_duty: true,
        consultant_ids: []
      })
    }
  }, [id, isOpen])

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSaving(true)

    if (form.has_duty && form.consultant_ids.length === 0) {
      toast({
        title: 'Selecione pelo menos um consultor',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      setSaving(false)
      return
    }

    try {
      if (id) {
        await service.update(id, form)
        toast({ title: 'Feriado/Plantão atualizado com sucesso', status: 'success', duration: 3000, isClosable: true })
      } else {
        await service.create(form)
        toast({ title: 'Feriado/Plantão cadastrado com sucesso', status: 'success', duration: 3000, isClosable: true })
      }
      onClose()
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = err as any
      if (error.response?.status === 422) setFormErrors(error.response.data.errors)
      else toast({ title: 'Erro ao salvar feriado/plantão', status: 'error', duration: 3000, isClosable: true })
    } finally {
      setSaving(false)
    }
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <form onSubmit={handleSave}>
        <ModalContent>
          <ModalHeader>{id ? 'Editar' : 'Cadastrar'} Feriado/Plantão</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={3}>
              <Box>
                <FormLabel>Título (opcional)</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  disabled={saving}
                />
                {formErrors.title && <Text color="red.500">{formErrors.title}</Text>}
              </Box>
              <Box>
                <FormLabel>Data</FormLabel>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  disabled={saving}
                  required
                />
                {formErrors.date && <Text color="red.500">{formErrors.date}</Text>}
              </Box>
              <Box>
                <Checkbox
                  colorScheme="magenta"
                  isChecked={form.has_duty}
                  onChange={(e) => setForm({ ...form, has_duty: e.target.checked })}
                  isDisabled={saving}
                >
                  Possui plantão?
                </Checkbox>
                {formErrors.has_duty && <Text color="red.500">{formErrors.has_duty}</Text>}
              </Box>
              {form.has_duty && (
                <Box>
                  <FormLabel>Consultores de Plantão</FormLabel>
                  <Flex
                    direction="column"
                    gap={2}
                    maxH="200px"
                    overflowY="auto"
                    border="1px solid #eee"
                    borderRadius="md"
                    p={2}
                  >
                    {commercialConsultants.map((consultant) => (
                      <Checkbox
                        key={consultant.id}
                        colorScheme="magenta"
                        isChecked={form.consultant_ids.includes(consultant.id)}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setForm((prev) => ({
                            ...prev,
                            consultant_ids: checked
                              ? [...prev.consultant_ids, consultant.id]
                              : prev.consultant_ids.filter((id) => id !== consultant.id)
                          }))
                        }}
                        isDisabled={saving}
                      >
                        {consultant.name}
                      </Checkbox>
                    ))}
                  </Flex>
                  {formErrors.consultants && <Text color="red.500">{formErrors.consultants}</Text>}
                </Box>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="magenta" mr={3} isLoading={saving} type="submit" disabled={saving}>
              Salvar
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={saving} type="button">
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </ChakraModal>
  )
}
